import React, { useMemo } from 'react';
import {
  ArrowLeft,
  Download,
  ExternalLink,
  Globe,
  MousePointerClick,
  ShieldCheck,
  Sparkles,
  Store,
  Wifi,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IpeBrandMark } from '@/components/ipe/IpeBrandLogo';
import { useIpeAuth } from '@/context/IpeAuthContext';
import { IPE_LOGIN_PATH, IPE_WORKBENCH_PATH, openUrlInNewTab } from '@/config/ipeRoutes';
import { resolvePluginStoreTarget } from '@/utils/pluginBrowserStore';
import '@/styles/ipe/plugin-install.css';

const FEATURE_DEFS = [
  { icon: Sparkles, titleKey: 'featureSmartTitle', descKey: 'featureSmartDesc' },
  { icon: ShieldCheck, titleKey: 'featureBindTitle', descKey: 'featureBindDesc' },
  { icon: MousePointerClick, titleKey: 'featureManualTitle', descKey: 'featureManualDesc' },
  { icon: Globe, titleKey: 'featureLinkTitle', descKey: 'featureLinkDesc' },
];

function BrowserBadge({ prefix, label }) {
  return (
    <span className="ipe-plugin-install__browser-badge">
      {prefix}
      {label}
    </span>
  );
}

export default function PluginInstallPage() {
  const { t } = useTranslation('workbench');
  const { isAuthenticated } = useIpeAuth();
  const tp = useMemo(() => (key, opts) => t(`pluginInstallPage.${key}`, opts), [t]);
  const storeTarget = useMemo(() => resolvePluginStoreTarget(), []);

  const browserLabel = storeTarget.browserLabel
    || (storeTarget.browserLabelKey ? tp(storeTarget.browserLabelKey) : '');
  const storeLabel = storeTarget.storeLabelKey ? tp(storeTarget.storeLabelKey) : '';
  const buttonText = tp(storeTarget.buttonTextKey);
  const storeHint = storeTarget.storeHintKey ? tp(storeTarget.storeHintKey) : '';
  const installAction = storeTarget.installActionKey ? tp(storeTarget.installActionKey) : '';

  const installSteps = useMemo(() => {
    const steps = [tp('stepLogin')];
    if (storeTarget.needsProxy) {
      steps.push(tp('stepOpenProxy'));
    } else {
      steps.push(tp('stepOpenStore'));
    }
    if (storeHint) {
      steps.push(storeHint);
    }
    if (installAction) {
      steps.push(tp('stepClickInstall', { action: installAction }));
    }
    steps.push(tp('stepSync'));
    return steps;
  }, [installAction, storeHint, storeTarget.needsProxy, tp]);

  const openInstall = () => {
    if (!storeTarget.supported) return;
    const url = storeTarget.storeUrl || storeTarget.offlineUrl;
    if (url) openUrlInNewTab(url);
  };

  const backPath = isAuthenticated ? IPE_WORKBENCH_PATH : IPE_LOGIN_PATH;
  const backLabel = isAuthenticated ? tp('backWorkbench') : tp('backLogin');

  return (
    <div className="ipe-plugin-install">
      <div className="ipe-plugin-install__bg" aria-hidden="true">
        <div className="ipe-plugin-install__bg-orb ipe-plugin-install__bg-orb--a" />
        <div className="ipe-plugin-install__bg-orb ipe-plugin-install__bg-orb--b" />
        <div className="ipe-plugin-install__bg-grid" />
      </div>

      <header className="ipe-plugin-install__topbar">
        <Link to={backPath} className="ipe-plugin-install__back">
          <ArrowLeft size={16} aria-hidden="true" />
          {backLabel}
        </Link>
        <IpeBrandMark className="ipe-plugin-install__brand" />
      </header>

      <main className="ipe-plugin-install__main">
        <section className="ipe-plugin-install__hero">
          <p className="ipe-plugin-install__eyebrow">Browser Extension</p>
          <h1 className="ipe-plugin-install__title">{tp('title')}</h1>
          <p className="ipe-plugin-install__lead">{tp('lead')}</p>

          <div className="ipe-plugin-install__feature-grid">
            {FEATURE_DEFS.map(({ icon: Icon, titleKey, descKey }) => (
              <article key={titleKey} className="ipe-plugin-install__feature">
                <div className="ipe-plugin-install__feature-icon" aria-hidden="true">
                  <Icon size={18} strokeWidth={1.8} />
                </div>
                <h2 className="ipe-plugin-install__feature-title">{tp(titleKey)}</h2>
                <p className="ipe-plugin-install__feature-desc">{tp(descKey)}</p>
              </article>
            ))}
          </div>
        </section>

        <aside className="ipe-plugin-install__panel">
          <div className="ipe-plugin-install__panel-card">
            <div className="ipe-plugin-install__panel-head">
              <div className="ipe-plugin-install__panel-icon" aria-hidden="true">
                <Store size={22} strokeWidth={1.6} />
              </div>
              <div>
                <h2 className="ipe-plugin-install__panel-title">{tp('installTitle')}</h2>
                <BrowserBadge prefix={tp('detectedBrowser')} label={browserLabel} />
              </div>
            </div>

            {storeTarget.needsProxy ? (
              <div className="ipe-plugin-install__proxy-alert" role="note">
                <Wifi size={18} strokeWidth={1.8} aria-hidden="true" />
                <div>
                  <strong>{tp('proxyTitle')}</strong>
                  <p>{tp('proxyBody')}</p>
                </div>
              </div>
            ) : null}

            {!storeTarget.supported ? (
              <div className="ipe-plugin-install__unsupported" role="alert">
                <p>{storeHint}</p>
              </div>
            ) : null}

            <button
              type="button"
              className="ipe-plugin-install__cta"
              onClick={openInstall}
              disabled={!storeTarget.supported || (!storeTarget.storeUrl && !storeTarget.offlineUrl)}
            >
              <Download size={18} strokeWidth={1.8} aria-hidden="true" />
              {buttonText}
              {storeTarget.supported ? (
                <ExternalLink size={14} strokeWidth={2} aria-hidden="true" />
              ) : null}
            </button>

            {storeTarget.supported && storeLabel ? (
              <p className="ipe-plugin-install__store-meta">
                {tp('jumpToStore')}
                {' '}
                <strong>{storeLabel}</strong>
              </p>
            ) : null}

            <ol className="ipe-plugin-install__steps">
              {installSteps.map((step, index) => (
                <li key={step}>
                  <span className="ipe-plugin-install__step-num">{index + 1}</span>
                  <span className="ipe-plugin-install__step-text">{step}</span>
                </li>
              ))}
            </ol>

            <p className="ipe-plugin-install__privacy">{tp('privacy')}</p>

            {storeTarget.offlineUrl ? (
              <p className="ipe-plugin-install__fallback">
                {tp('offlinePrompt')}
                {' '}
                <a
                  href={storeTarget.offlineUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {tp('offlineDownload')}
                </a>
              </p>
            ) : null}
          </div>
        </aside>
      </main>
    </div>
  );
}
